import { sourceFingerprint } from 'virtual:world-plan-source';
import bakedText from '../data/worldPlans.generated.json?raw';
import type { AppData } from '../data/types';
import { sha256Hex } from '../core/sha256';
import type { SiteModel } from '../site/types';
import type { PolygonWithHoles, Vec2 } from '../geo/types';
import type { PlannedLots } from './fabric/fabricLots';
import type { HogeHuisYardPlan } from './fabric/hogeHuisYard';
import type { LifePlan } from './life/types';
import type { BakedWaterPlan, WaterPlan } from './terrain/waterPlan';
import type { PavedGround, PavingCoordinates } from './streetscape/pavingPieces';
import { framedStreets } from './streetscape/pavingGround';
import { planJson } from './planJson';
import {
  restoreTrianglePlans,
  type PackedTrianglePlans,
  type TrianglePlans,
} from './terrain/trianglePlans';

export const WORLD_PLAN_VERSION = 3;
export interface BakedWorldPlans {
  readonly version: number;
  readonly seed: number;
  readonly sourceFingerprint: string;
  readonly inputFingerprint: string;
  readonly lots: PlannedLots;
  readonly hogeHuis?: HogeHuisYardPlan;
  readonly life: LifePlan;
  readonly terrain: {
    readonly ground: PackedTrianglePlans;
    readonly paving: PackedTrianglePlans;
    readonly water?: BakedWaterPlan;
  };
  readonly paving: {
    readonly ground: readonly { source: number; polygon: PolygonWithHoles | null }[];
    readonly pieces: readonly {
      polygon: PolygonWithHoles;
      ground: number;
      seed: number;
      front: string | null;
      street: string | null;
      coordinates?: PavingCoordinates;
    }[];
  };
}

// Parse the authored numeric text directly; Vite's named JSON exports normalize -0 to +0.
const bakedData = JSON.parse(bakedText) as BakedWorldPlans;

export async function worldPlanInputFingerprint(data: AppData, site: SiteModel): Promise<string> {
  const input = planJson({
    data,
    site: {
      bounds: site.bounds,
      water: site.water,
      // Planning reads geometry/identity, and hydration keeps the current ground styles. Derived
      // garden ditch angles use atan2, which can differ by one ULP between JS engines.
      ground: site.ground.map(({ id, index, regionId, key, kind, polygon }) => ({
        id,
        index,
        regionId,
        key,
        kind,
        polygon,
      })),
      waterEdges: site.waterEdges,
      streets: site.streets,
      buildable: site.buildable,
      bridges: site.bridges,
      damDecks: site.damDecks,
      waterAccessDecks: site.waterAccessDecks,
    },
  });
  const bytes = new TextEncoder().encode(input);
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return sha256Hex(bytes);
  const hash = await subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function bakePaving(paving: PavedGround, site: SiteModel): BakedWorldPlans['paving'] {
  const sources = new Map(site.ground.map((piece) => [piece.index, piece]));
  return {
    ground: paving.ground.map((piece) => ({
      source: piece.index,
      polygon: piece.polygon === sources.get(piece.index)!.polygon ? null : piece.polygon,
    })),
    pieces: paving.pieces.map(({ polygon, ground, seed, front, street, coordinates }) => ({
      polygon,
      ground: ground.index,
      seed,
      front,
      street,
      ...(coordinates ? { coordinates } : {}),
    })),
  };
}

export function restorePaving(
  paving: BakedWorldPlans['paving'],
  data: AppData,
  site: SiteModel
): PavedGround {
  const sources = new Map(site.ground.map((piece) => [piece.index, piece]));
  const frames = new Map(
    framedStreets(data.layout.streets).map(({ street, frame }) => [street.id, frame])
  );
  return {
    ground: paving.ground.map(({ source, polygon }) => {
      const piece = sources.get(source);
      if (!piece) throw new Error('Baked ground source is missing');
      return polygon ? { ...piece, polygon } : piece;
    }),
    pieces: paving.pieces.map(({ ground, ...piece }) => {
      const source = sources.get(ground),
        frame = piece.street ? frames.get(piece.street) : undefined;
      if (!source || (piece.street && (!frame || !piece.coordinates)))
        throw new Error('Baked paving source is missing');
      const coordsAt = piece.coordinates
        ? (p: Vec2): Vec2 => {
            const { near, edgeAt } = piece.coordinates!;
            const [s, t] = frame!.coordsOf(p, near);
            return [s, edgeAt - t];
          }
        : (p: Vec2): Vec2 => [p[0], p[1]];
      return { ...piece, ground: source, coordsAt };
    }),
  };
}

export async function compiledWorldPlans(
  data: AppData,
  site: SiteModel,
  seed: number,
  baked: BakedWorldPlans = bakedData,
  source: string = sourceFingerprint
): Promise<{
  lots: PlannedLots;
  hogeHuis: HogeHuisYardPlan | undefined;
  life: LifePlan;
  paving: PavedGround;
  terrain: { ground: TrianglePlans; paving: TrianglePlans; water?: WaterPlan };
} | null> {
  if (
    baked.version !== WORLD_PLAN_VERSION ||
    baked.seed !== seed ||
    baked.sourceFingerprint !== source
  )
    return null;
  try {
    if (baked.inputFingerprint !== (await worldPlanInputFingerprint(data, site))) return null;
    return {
      lots: baked.lots,
      hogeHuis: baked.hogeHuis,
      life: baked.life,
      paving: restorePaving(baked.paving, data, site),
      terrain: {
        ground: restoreTrianglePlans(baked.terrain.ground),
        paving: restoreTrianglePlans(baked.terrain.paving),
        ...(baked.terrain.water
          ? {
              water: {
                triangles: restoreTrianglePlans(baked.terrain.water.triangles),
                labels: baked.terrain.water.labels,
              },
            }
          : {}),
      },
    };
  } catch {
    return null;
  }
}
